import { useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import 'dayjs/locale/ru'
import 'dayjs/locale/en'
import { groupsApi } from '@/api/groups'
import { lessonsApi } from '@/api/lessons'
import { usersApi } from '@/api/users'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import type { Lesson, LessonScope, LessonStatus, StudentGroup, User } from '@/types'
import { ROUTES } from '@/utils/constants'
import '@/styles/dashboard.css'
import {
  Alert,
  Box,
  Breadcrumbs,
  Autocomplete,
  Button,
  Chip,
  Collapse,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  Menu,
  MenuItem,
  Popover,
  Paper,
  Skeleton,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { Home as HomeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material'
import {
  Cancel as CancelIcon,
  EventRepeat as EventRepeatIcon,
  MoreVert as MoreVertIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material'
import { LessonRescheduledChip } from '@/components/lessons/LessonRescheduledChip'
import { LessonRescheduleDialog } from '@/components/lessons/LessonRescheduleDialog'
import { useTheme } from '@mui/material/styles'
import { useMediaQuery } from '@mui/material'
import { getErrorMessage } from '@/utils/error'
import { dateTimeLocalStringToIso, formatLessonTimeRange } from '@/utils/date'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { FiltersButton } from '@/components/common/FiltersButton'
import { LessonPricePreview } from '@/components/billing/LessonPricePreview'
import { useAuthStore } from '@/stores/authStore'
import {
  useTeachingLessonsListRealtime,
  type LessonStatusChangedPayload,
} from '@/realtime/useLessonRealtime'

type OwnerType = 'all' | 'user' | 'group'

const statusOrder: LessonStatus[] = ['planned', 'in_progress', 'completed', 'canceled']

export function LessonsPage() {
  const { t, i18n } = useTranslation()
  const authUserId = useAuthStore((s) => s.user?.id) ?? null
  const roles = useAuthStore((s) => s.user?.roles) ?? []
  const isAdmin = roles.some((r) => r.slug === 'admin')
  const isStudent = roles.some((r) => r.slug === 'student')
  const isTeacher = roles.some((r) => r.slug === 'teacher')
  const showRoleTabs = isStudent && isTeacher
  const [, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const theme = useTheme()
  const menuItemSelectedSx = {
    '&.Mui-selected': {
      // Берем из theme (palette), чтобы не использовать hardcoded цвета.
      color: theme.palette.primary.contrastText,
    },
  }
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const adapterLocale = i18n.language === 'ru' ? 'ru' : 'en'

  const [lessons, setLessons] = useState<Lesson[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [fromDate, setFromDate] = useState<string>('')
  const [toDate, setToDate] = useState<string>('')
  const [ownerType, setOwnerType] = useState<OwnerType>('all')
  const [ownerId, setOwnerId] = useState<number | ''>('')
  const [statusFilter, setStatusFilter] = useState<LessonStatus[]>(['planned', 'in_progress'])

  const [students, setStudents] = useState<User[]>([])
  const [groups, setGroups] = useState<StudentGroup[]>([])

  const [filtersOpen, setFiltersOpen] = useState(false)
  const [dateRangeAnchorEl, setDateRangeAnchorEl] = useState<HTMLElement | null>(null)
  const [page, setPage] = useState(0)
  const [perPage, setPerPage] = useState(50)
  const [total, setTotal] = useState(0)

  const [createOpen, setCreateOpen] = useState(false)
  const [createSubmitting, setCreateSubmitting] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [createScope, setCreateScope] = useState<LessonScope>('individual')
  const [createOwnerId, setCreateOwnerId] = useState<number | ''>('')
  const [createStartAt, setCreateStartAt] = useState('')
  const [createEndAt, setCreateEndAt] = useState('')
  // Пока учитель не правил «Окончание» руками, оно следует за началом (+1 час)
  const [createEndTouched, setCreateEndTouched] = useState(false)
  const [createParticipantIds, setCreateParticipantIds] = useState<number[]>([])

  const [actionsAnchorEl, setActionsAnchorEl] = useState<HTMLElement | null>(null)
  const [actionsLesson, setActionsLesson] = useState<Lesson | null>(null)
  const [rescheduleLesson, setRescheduleLesson] = useState<Lesson | null>(null)

  const appliedFiltersCount = useMemo(() => {
    let count = 0

    // Статусы по умолчанию уже считаются "применёнными фильтрами"
    if (statusFilter.length > 0) count += 1

    if (fromDate || toDate) count += 1

    if (ownerType !== 'all') count += 1
    if (ownerId !== '') count += 1

    return count
  }, [fromDate, ownerId, ownerType, statusFilter, toDate])

  const hasActiveFilters = appliedFiltersCount > 0

  const ownerScope: LessonScope | undefined = useMemo(() => {
    if (ownerType === 'user') return 'individual'
    if (ownerType === 'group') return 'group'
    return undefined
  }, [ownerType])

  const statusLabel = (s: LessonStatus) => {
    if (s === 'planned') return t('lessons.status.planned', 'planned')
    if (s === 'in_progress') return t('lessons.status.in_progress', 'in progress')
    if (s === 'completed') return t('lessons.status.completed', 'completed')
    return t('lessons.status.canceled', 'canceled')
  }

  const openDateRange = (e: MouseEvent<HTMLElement>) => {
    setDateRangeAnchorEl(e.currentTarget)
  }

  const closeDateRange = () => {
    setDateRangeAnchorEl(null)
  }

  const dateCalendarValue: Dayjs | null = toDate ? dayjs(toDate) : fromDate ? dayjs(fromDate) : null

  const dateRangeLabel = useMemo(() => {
    if (fromDate && toDate) {
      return `${dayjs(fromDate).format('DD.MM.YYYY')} - ${dayjs(toDate).format('DD.MM.YYYY')}`
    }
    if (fromDate) {
      return `с ${dayjs(fromDate).format('DD.MM.YYYY')}`
    }
    if (toDate) {
      return `до ${dayjs(toDate).format('DD.MM.YYYY')}`
    }
    return ''
  }, [fromDate, toDate])

  const handleDateCalendarChange = (value: Dayjs | null) => {
    if (!value) return
    const picked = value.format('YYYY-MM-DD')

    if (!fromDate || toDate) {
      setFromDate(picked)
      setToDate('')
      return
    }

    // fromDate задан, toDate пустой
    if (picked < fromDate) {
      setFromDate(picked)
      setToDate(fromDate)
    } else {
      setToDate(picked)
    }
    closeDateRange()
  }

  useEffect(() => {
    let cancelled = false
    const loadFilters = async () => {
      try {
        const [usersRes, groupsRes] = await Promise.all([
          usersApi.getAll({ role: 'student', per_page: 100, page: 1 }),
          groupsApi.getAll({ status: 'active', per_page: 100, page: 1 }),
        ])
        if (!cancelled) {
          setStudents(usersRes.data)
          setGroups(groupsRes.data)
        }
      } catch (e) {
        // Owner filter is optional; ignore errors here.
      }
    }
    loadFilters().catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    setPage(0)
  }, [fromDate, toDate, ownerId, ownerScope, statusFilter])

  const fetchLessons = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await lessonsApi.index({
        from: fromDate || undefined,
        to: toDate || undefined,
        lesson_scope: ownerScope,
        recipient_id: ownerId === '' ? undefined : ownerId,
        status: statusFilter.length ? statusFilter : undefined,
        per_page: perPage,
        page: page + 1,
      })
      setLessons(response.data)
      setTotal(response.total ?? response.data.length)
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('fallback.error', 'Ошибка'))
    } finally {
      setLoading(false)
    }
  }, [fromDate, ownerId, ownerScope, page, perPage, statusFilter, toDate, t])

  useEffect(() => {
    fetchLessons().catch(() => {})
  }, [fetchLessons])

  /**
   * Оптимистичное применение события смены статуса. Само событие несёт
   * всё нужное, поэтому строка обновляется сразу и не зависит от того, что
   * вернёт повторный запрос списка.
   */
  const applyLessonStatusEvent = useCallback((payload: LessonStatusChangedPayload) => {
    setLessons((prev) =>
      prev.map((lesson) =>
        lesson.id === payload.lesson_id
          ? {
              ...lesson,
              status: payload.status,
              actual_start_at: payload.actual_start_at,
              actual_end_at: payload.actual_end_at,
            }
          : lesson,
      ),
    )
  }, [])

  /**
   * Пока учитель на списке (не в карточке урока), иначе не было бы subscribe
   * на `private-lesson.{id}.status`. Канал статуса, а не presence урока:
   * presence ставит отметку присутствия на занятии, и списку она не положена.
   */
  useTeachingLessonsListRealtime(lessons, authUserId, isAdmin, fetchLessons, applyLessonStatusEvent)

  const handleStatusChange = (e: SelectChangeEvent<typeof statusFilter>) => {
    const value = e.target.value
    // MUI multi-select обычно отдаёт массив; обрабатываем на всякий случай строку.
    if (typeof value === 'string') {
      setStatusFilter(value ? ([value] as LessonStatus[]) : [])
      return
    }
    setStatusFilter(value as LessonStatus[])
  }

  const handleStart = async (lessonId: number) => {
    try {
      await lessonsApi.start(lessonId)
      await fetchLessons()
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('lessons.startError'))
    }
  }

  const handleFinish = async (lessonId: number) => {
    try {
      await lessonsApi.finish(lessonId)
      // Дальше реальная логика (модалка ДЗ) реализуется на странице деталей.
      navigate(`${ROUTES.LESSON_DETAILS(lessonId)}?after_finish=1`)
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('lessons.finishError'))
    }
  }

  const handleCancel = async (lessonId: number) => {
    if (!window.confirm(t('lessons.cancelConfirm', 'Отменить урок?'))) return
    try {
      await lessonsApi.cancel(lessonId)
      await fetchLessons()
    } catch (e: unknown) {
      setError(getErrorMessage(e) || t('lessons.cancelError'))
    }
  }

  const canManageLesson = (lesson: Lesson) => isAdmin || lesson.teacher_id === authUserId

  const openActionsMenu = (e: MouseEvent<HTMLElement>, lesson: Lesson) => {
    setActionsAnchorEl(e.currentTarget)
    setActionsLesson(lesson)
  }

  const closeActionsMenu = () => {
    setActionsAnchorEl(null)
    setActionsLesson(null)
  }

  const resetCreateForm = () => {
    setCreateError(null)
    setCreateScope('individual')
    setCreateOwnerId('')
    setCreateStartAt('')
    setCreateEndAt('')
    setCreateEndTouched(false)
    setCreateParticipantIds([])
  }

  const openCreateModal = () => {
    resetCreateForm()
    setCreateOpen(true)
  }

  const handleCreateLesson = async () => {
    setCreateError(null)
    if (createOwnerId === '') {
      setCreateError(t('lessons.create.errorOwner', 'Выберите владельца'))
      return
    }
    if (!createStartAt || !createEndAt) {
      setCreateError(t('lessons.create.errorDates', 'Укажите дату и время начала/окончания'))
      return
    }

    let participantIds = createParticipantIds
    if (createScope === 'group') {
      const group = groups.find((g) => g.id === Number(createOwnerId))
      participantIds =
        group?.members?.map((m) => m.student?.id).filter((x): x is number => typeof x === 'number') ?? []
    }

    if (participantIds.length === 0) {
      setCreateError(t('lessons.create.errorParticipants', 'Добавьте хотя бы одного участника'))
      return
    }

    setCreateSubmitting(true)
    try {
      await lessonsApi.create({
        lesson_scope: createScope,
        recipient_id: Number(createOwnerId),
        planned_start_at: dateTimeLocalStringToIso(createStartAt),
        planned_end_at: dateTimeLocalStringToIso(createEndAt),
        participants: participantIds.map((userId) => ({
          user_id: userId,
        })),
      })

      setCreateOpen(false)
      await fetchLessons()
    } catch (e: unknown) {
      setCreateError(getErrorMessage(e) || 'Ошибка создания урока')
    } finally {
      setCreateSubmitting(false)
    }
  }

  const ownerValue = ownerId === '' ? '' : String(ownerId)

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 }, pb: 3 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link component={RouterLink} to={ROUTES.DASHBOARD} underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Typography color="text.primary">{t('lessons.title', 'Уроки')}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('lessons.title', 'Уроки')}
          </Typography>
          <PageHelpPopover pageKey="lessons" />
        </Box>
        {showRoleTabs && (
          <Tabs
            value="teaching"
            onChange={(_, v: string) => setSearchParams({ tab: v })}
            sx={{ mb: 2 }}
          >
            <Tab value="student" label={t('dashboard.student', 'Ученик')} />
            <Tab value="teaching" label={t('dashboard.teacher', 'Преподаватель')} />
          </Tabs>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FiltersButton
            open={filtersOpen}
            count={appliedFiltersCount}
            onToggle={() => setFiltersOpen((o) => !o)}
            label={t('lessons.filtersButton', 'Фильтры')}
          />

          <Box sx={{ ml: isMobile ? 0 : 'auto', display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small" onClick={openCreateModal} sx={{ height: 40 }}>
              {t('lessons.create.button', 'Создать урок')}
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Collapse in={filtersOpen}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2, px: 1, py: 1 }}>
                <TextField
                  size="small"
                  label={t('lessons.filterPeriod', 'Период')}
                  value={dateRangeLabel}
                  InputProps={{ readOnly: true }}
                  onClick={openDateRange}
                  sx={{ minWidth: { xs: '100%', sm: 220 }, cursor: 'pointer' }}
                />

                <Popover
                  open={Boolean(dateRangeAnchorEl)}
                  anchorEl={dateRangeAnchorEl}
                  onClose={closeDateRange}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  <Box sx={{ p: 1 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
                      <DateCalendar
                        value={dateCalendarValue}
                        views={['month', 'day']}
                        onChange={(v) => void handleDateCalendarChange(v)}
                      />
                    </LocalizationProvider>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                          setFromDate('')
                          setToDate('')
                          closeDateRange()
                        }}
                      >
                        {t('common.reset', 'Сбросить')}
                      </Button>
                      <Button size="small" variant="contained" color="secondary" onClick={closeDateRange}>
                        {t('common.ok', 'Ок')}
                      </Button>
                    </Box>
                  </Box>
                </Popover>

                <FormControl size="small" sx={{ minWidth: 260 }}>
                  <InputLabel>{t('lessons.filterStudent', 'Ученик')}</InputLabel>
                  <Select
                    value={ownerType === 'user' ? ownerValue : ''}
                    label={t('lessons.filterStudent', 'Ученик')}
                    onChange={(e) => {
                      const nextId = e.target.value ? Number(e.target.value) : ''
                      setOwnerType(nextId === '' ? 'all' : 'user')
                      setOwnerId(nextId)
                    }}
                  >
                    <MenuItem value="" sx={menuItemSelectedSx}>
                      {t('common.none', '—')}
                    </MenuItem>
                    {students.map((s) => (
                      <MenuItem key={s.id} value={String(s.id)} sx={menuItemSelectedSx}>
                        {s.display_name ?? [s.surname, s.first_name].filter(Boolean).join(' ') ?? s.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>{t('lessons.filterFormat', 'Формат')}</InputLabel>
                  <Select
                    value={ownerType}
                    label={t('lessons.filterFormat', 'Формат')}
                    onChange={(e) => {
                      const nextType = e.target.value as OwnerType
                      setOwnerType(nextType)
                      if (nextType !== ownerType) {
                        setOwnerId('')
                      }
                    }}
                  >
                    <MenuItem value="all" sx={menuItemSelectedSx}>
                      {t('common.all', 'Все')}
                    </MenuItem>
                    <MenuItem value="user" sx={menuItemSelectedSx}>
                      {t('lessons.scopeIndividual', 'Индивидуальный')}
                    </MenuItem>
                    <MenuItem value="group" sx={menuItemSelectedSx}>
                      {t('lessons.scopeGroup', 'Групповой')}
                    </MenuItem>
                  </Select>
                </FormControl>

                {ownerType === 'group' && (
                  <FormControl size="small" sx={{ minWidth: 260 }}>
                    <InputLabel>{t('lessons.filterGroup', 'Группа')}</InputLabel>
                    <Select
                      value={ownerValue}
                      label={t('lessons.filterGroup', 'Группа')}
                      onChange={(e) => setOwnerId(e.target.value ? Number(e.target.value) : '')}
                    >
                      <MenuItem value="" sx={menuItemSelectedSx}>
                        {t('common.none', '—')}
                      </MenuItem>
                      {groups.map((g) => (
                        <MenuItem key={g.id} value={String(g.id)} sx={menuItemSelectedSx}>
                          {g.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <FormControl size="small" sx={{ minWidth: 240 }}>
                  <InputLabel>{t('lessons.filterStatus', 'Статус')}</InputLabel>
                  <Select
                    multiple
                    value={statusFilter}
                    label={t('lessons.filterStatus', 'Статус')}
                    onChange={handleStatusChange}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as LessonStatus[]).map((s) => (
                          <Chip key={s} size="small" label={statusLabel(s)} />
                        ))}
                      </Box>
                    )}
                  >
                    {statusOrder.map((s) => (
                      <MenuItem key={s} value={s} sx={menuItemSelectedSx}>
                        <Checkbox
                          size="small"
                          checked={statusFilter.includes(s)}
                          sx={{
                            mr: 1,
                            color: 'inherit',
                            '&.Mui-checked': { color: 'inherit' },
                          }}
                        />
                        {statusLabel(s)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Фильтры применяются автоматически при изменении */}
              </Box>
        </Collapse>

        {loading ? (
          <Box sx={{ width: '100%' }}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow hover={false} sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                    {Array.from({ length: 5 }).map((_, idx) => (
                      <TableCell key={idx}>
                        <Skeleton variant="text" width={idx === 0 ? 120 : 100} />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: 5 }).map((_, rowIdx) => (
                    <TableRow key={rowIdx}>
                      {Array.from({ length: 5 }).map((__, colIdx) => (
                        <TableCell key={colIdx}>
                          <Skeleton variant="text" width={colIdx === 4 ? 90 : 120} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <TableContainer component={Paper}>
          <Table>
              <TableHead>
                <TableRow hover={false} sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                  <TableCell>{t('lessons.tablePlannedAt', 'Дата урока')}</TableCell>
                  <TableCell>{t('lessons.tableParticipants', 'Участники')}</TableCell>
                  <TableCell>{t('lessons.tableScope', 'Формат')}</TableCell>
                  <TableCell>{t('lessons.tableStatus', 'Статус')}</TableCell>
                  <TableCell align="right">{t('common.actions', 'Действия')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lessons.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography color="text.secondary">
                        {hasActiveFilters
                          ? t('lessons.emptyByFilters', 'По вашим фильтрам ничего не найдено, ослабьте фильтры')
                          : t('lessons.empty', 'Уроки не найдены')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  lessons.map((lesson) => {
                    const scopeLabel = lesson.lesson_scope === 'individual' ? t('lessons.scopeIndividual', 'Индивидуальный') : t('lessons.scopeGroup', 'Групповой')
                    const participants = lesson.participants ?? []

                    return (
                      <TableRow key={lesson.id}>
                        <TableCell>
                          {/* Дата — прямой вход в урок: раньше открыть его можно
                              было только через меню «три точки», и в списке из
                              десятков уроков это лишний клик на каждом */}
                          <Link
                            component={RouterLink}
                            to={ROUTES.LESSON_DETAILS(lesson.id)}
                            underline="hover"
                            sx={{ fontSize: 14 }}
                          >
                            {formatLessonTimeRange(lesson.planned_start_at, lesson.planned_end_at)}
                          </Link>
                        </TableCell>
                        <TableCell>
                          {participants.length === 0 ? (
                            <Typography color="text.secondary">—</Typography>
                          ) : (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                              {participants.map((p) => {
                                const u = p.user
                                const label =
                                  u?.display_name ??
                                  [u?.surname, u?.first_name].filter((v): v is string => Boolean(v)).join(' ') ??
                                  u?.nickname ??
                                  u?.email ??
                                  `#${p.user_id}`

                                return (
                                  <Link
                                    key={p.id}
                                    component={RouterLink}
                                    to={`/users/${p.user_id}`}
                                    underline="hover"
                                    title={label}
                                    sx={{ fontSize: 14, wordBreak: 'break-word' }}
                                  >
                                    {label}
                                  </Link>
                                )
                              })}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>{scopeLabel}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                            {statusLabel(lesson.status)}
                            <LessonRescheduledChip lesson={lesson} />
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {(isTeacher || isAdmin) && (
                            <IconButton
                              size="small"
                              aria-label={t('common.actions', 'Действия')}
                              onClick={(e) => openActionsMenu(e, lesson)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!loading && total > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', mt: 2 }}>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={perPage}
              onRowsPerPageChange={(e) => {
                setPerPage(parseInt(e.target.value, 10))
                setPage(0)
              }}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage={t('words.rows_per_page', { defaultValue: 'На странице' })}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} ${t('words.of', { defaultValue: 'из' })} ${count !== -1 ? count : to}`
              }
              sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}
            />
          </Box>
        )}
      </Box>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('lessons.create.title', 'Создание урока')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {createError && <Alert severity="error">{createError}</Alert>}

            <FormControl size="small">
              <InputLabel>{t('lessons.create.scope', 'Формат')}</InputLabel>
              <Select
                label={t('lessons.create.scope', 'Формат')}
                value={createScope}
                onChange={(e) => {
                  const nextScope = e.target.value as LessonScope
                  setCreateScope(nextScope)
                  setCreateOwnerId('')
                  setCreateParticipantIds([])
                }}
              >
                <MenuItem value="individual" sx={menuItemSelectedSx}>
                  {t('lessons.scopeIndividual', 'Индивидуальный')}
                </MenuItem>
                <MenuItem value="group" sx={menuItemSelectedSx}>
                  {t('lessons.scopeGroup', 'Групповой')}
                </MenuItem>
              </Select>
            </FormControl>

            {createScope === 'individual' ? (
              <Autocomplete
                options={students}
                value={students.find((s) => s.id === createOwnerId) ?? null}
                size="small"
                getOptionLabel={(s) => s.display_name ?? [s.surname, s.first_name].filter(Boolean).join(' ') ?? s.email}
                onChange={(_, v) => {
                  const id = v?.id ?? ''
                  setCreateOwnerId(id)
                  setCreateParticipantIds(v ? [v.id] : [])
                }}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderInput={(params) => (
                  <TextField {...params} label={t('lessons.create.ownerStudent', 'Участник')} />
                )}
              />
            ) : (
              <>
                <Autocomplete
                  options={groups}
                  value={groups.find((g) => g.id === createOwnerId) ?? null}
                  size="small"
                  getOptionLabel={(g) => g.name}
                  onChange={(_, v) => {
                    const id = v?.id ?? ''
                    setCreateOwnerId(id)
                    // Для группового урока участниками будут все участники группы.
                    const ids =
                      v?.members
                        ?.map((m) => m.student?.id)
                        .filter((x): x is number => typeof x === 'number') ?? []
                    setCreateParticipantIds(ids)
                  }}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  renderInput={(params) => (
                    <TextField {...params} label={t('lessons.create.ownerGroup', 'Группа')} />
                  )}
                />
              </>
            )}

            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
              <DateTimePicker
                label={t('lessons.create.startAt', 'Начало')}
                value={createStartAt ? dayjs(createStartAt) : null}
                onChange={(date) => {
                  setCreateStartAt(date ? date.format('YYYY-MM-DDTHH:mm') : '')
                  if (date && (!createEndTouched || !createEndAt)) {
                    setCreateEndAt(date.add(1, 'hour').format('YYYY-MM-DDTHH:mm'))
                  }
                }}
                minDateTime={dayjs()}
                ampm={false}
                slotProps={{
                  textField: {
                    size: 'small',
                  },
                }}
              />
              <DateTimePicker
                label={t('lessons.create.endAt', 'Окончание')}
                value={createEndAt ? dayjs(createEndAt) : null}
                onChange={(date) => {
                  setCreateEndAt(date ? date.format('YYYY-MM-DDTHH:mm') : '')
                  // Ручная правка фиксирует значение; очистка возвращает автоследование
                  setCreateEndTouched(Boolean(date))
                }}
                minDateTime={createStartAt ? dayjs(createStartAt) : dayjs()}
                ampm={false}
                slotProps={{
                  textField: {
                    size: 'small',
                  },
                }}
              />
            </LocalizationProvider>

            <LessonPricePreview
              recipientType={createScope === 'individual' ? 'user' : 'group'}
              recipientId={createOwnerId}
              plannedStartAt={createStartAt ? dateTimeLocalStringToIso(createStartAt) : ''}
              plannedEndAt={createEndAt ? dateTimeLocalStringToIso(createEndAt) : ''}
            />

            {/* Участники выбираются в зависимости от формата урока */}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={createSubmitting}>
            {t('common.cancel', 'Отмена')}
          </Button>
          <Button variant="contained" onClick={handleCreateLesson} disabled={createSubmitting}>
            {t('common.create', 'Создать')}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={actionsAnchorEl}
        open={Boolean(actionsAnchorEl)}
        onClose={closeActionsMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {actionsLesson && (
          <>
            <MenuItem
              onClick={() => {
                const lessonId = actionsLesson.id
                closeActionsMenu()
                navigate(ROUTES.LESSON_DETAILS(lessonId))
              }}
            >
              <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
              {t('common.view', 'Просмотр')}
            </MenuItem>
            {canManageLesson(actionsLesson) && actionsLesson.status === 'planned' && (
              <MenuItem
                onClick={() => {
                  const lessonId = actionsLesson.id
                  closeActionsMenu()
                  void handleStart(lessonId)
                }}
              >
                <PlayArrowIcon sx={{ mr: 1 }} fontSize="small" />
                {t('lessons.start', 'Начать')}
              </MenuItem>
            )}
            {canManageLesson(actionsLesson) &&
              (actionsLesson.status === 'planned' || actionsLesson.status === 'in_progress') && (
              <MenuItem
                onClick={() => {
                  const lessonId = actionsLesson.id
                  closeActionsMenu()
                  void handleFinish(lessonId)
                }}
              >
                <StopIcon sx={{ mr: 1 }} fontSize="small" />
                {t('lessons.finish', 'Завершить')}
              </MenuItem>
            )}
            {canManageLesson(actionsLesson) && actionsLesson.status === 'planned' && (
              <MenuItem
                onClick={() => {
                  setRescheduleLesson(actionsLesson)
                  closeActionsMenu()
                }}
              >
                <EventRepeatIcon sx={{ mr: 1 }} fontSize="small" />
                {t('lessons.reschedule.button', 'Перенести')}
              </MenuItem>
            )}
            {canManageLesson(actionsLesson) &&
              (actionsLesson.status === 'planned' || actionsLesson.status === 'in_progress') && (
                <MenuItem
                  onClick={() => {
                    const lessonId = actionsLesson.id
                    closeActionsMenu()
                    void handleCancel(lessonId)
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <CancelIcon sx={{ mr: 1 }} fontSize="small" />
                  {t('lessons.cancel', 'Отменить')}
                </MenuItem>
              )}
          </>
        )}
      </Menu>

      <LessonRescheduleDialog
        open={rescheduleLesson !== null}
        lesson={rescheduleLesson}
        onClose={() => setRescheduleLesson(null)}
        onSuccess={() => {
          setRescheduleLesson(null)
          void fetchLessons()
        }}
      />
    </DashboardLayout>
  )
}

